import { useState } from "react"
import { Link } from "react-router-dom"
import { motion, AnimatePresence } from "motion/react"
import { useRequest } from "../../../../hooks/useRequest"
import { userStore } from "../../../../store/userStore"
import { appStore } from "../../../../store/appStore"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

type PromoStatus = "idle" | "loading" | "success" | "error"

export default function Promocode() {
    const { requestData, request } = useRequest()
    const { user, setUser } = userStore()
    const { city } = appStore()

    const [code, setCode] = useState("")
    const [status, setStatus] = useState<PromoStatus>("idle")
    const [message, setMessage] = useState("")

    const card = user?.cards?.[0]

    /* ── Not logged in ───────────────────────────────────────────────────── */
    if (!user) {
        return (
            <div className="flex flex-col items-center gap-5 py-24 text-center">
                <span className="text-[64px]">🎟</span>
                <h3
                    className="text-white"
                    style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 20, letterSpacing: "-0.01em" }}>
                    Нужна авторизация
                </h3>
                <p className="text-white/50 text-[14px] max-w-[280px]" style={{ fontFamily: BODY }}>
                    Чтобы активировать промокод, войди в аккаунт
                </p>
                <Link
                    to="/auth"
                    className="rounded-full px-6 py-3 text-white text-[13px]"
                    style={{
                        background: "#e4206b",
                        fontFamily: MONO,
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        boxShadow: "0 0 18px rgba(228,32,107,0.4)",
                    }}>
                    Войти
                </Link>
            </div>
        )
    }

    /* ── No card ─────────────────────────────────────────────────────────── */
    if (!card) {
        return (
            <div className="flex flex-col items-center gap-5 py-24 text-center">
                <span className="text-[64px]">💳</span>
                <h3
                    className="text-white"
                    style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 20, letterSpacing: "-0.01em" }}>
                    Карта не найдена
                </h3>
                <p className="text-white/50 text-[14px] max-w-[280px]" style={{ fontFamily: BODY }}>
                    Для активации промокода нужна карта лояльности Мягкого кинотеатра
                </p>
            </div>
        )
    }

    /* ── Handle input — only alphanumeric ────────────────────────────────── */
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value.toUpperCase()
        if (/^[A-Z0-9]*$/.test(val)) {
            setCode(val)
            if (status !== "idle") setStatus("idle")
            setMessage("")
        }
    }

    /* ── Submit ──────────────────────────────────────────────────────────── */
    const submit = async () => {
        if (!code.trim() || status === "loading") return
        setStatus("loading")
        setMessage("")

        const res = await requestData({
            type: "POST",
            path: `user/card/${card.id}/promo`,
            sendData: { city_id: city.id, code },
            disableError: true,
        })

        if (res.status === "success") {
            setStatus("success")
            setMessage((res.data as { message?: string } | null)?.message ?? "Промокод успешно активирован!")
            setCode("")
            request("GET", `user?city_id=${city.id}`)
                .then(data => setUser(data))
                .catch(() => {})
        } else {
            setStatus("error")
            const errMsg =
                (res as any).error?.message ?? "Промокод не найден или уже использован"
            setMessage(errMsg)
        }
    }

    const disabled = !code.trim() || status === "loading"

    /* ── Card balance info ────────────────────────────────────────────────── */
    const balance = card.movie?.balance ?? 0
    const step = card.movie?.now_step_name ?? ""

    return (
        <div className="flex flex-col gap-5">
            {/* Page title */}
            <h2
                className="text-white"
                style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 22, letterSpacing: "-0.01em" }}>
                🎟 Промокод
            </h2>

            {/* Card badge */}
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-[22px] border border-white/10 bg-white/[0.05] p-5 flex items-center gap-4">
                {/* Card icon */}
                <div
                    className="size-12 rounded-[14px] flex items-center justify-center text-[22px] shrink-0"
                    style={{ background: "linear-gradient(135deg, #e4206b22, #349be622)", border: "1px solid rgba(255,255,255,0.1)" }}>
                    💎
                </div>
                <div className="flex flex-col gap-0.5 min-w-0">
                    <p
                        className="text-white/50 text-[11px] uppercase tracking-[0.08em]"
                        style={{ fontFamily: MONO, fontWeight: 600 }}>
                        Карта #{card.number}
                    </p>
                    <p
                        className="text-white font-bold"
                        style={{ fontFamily: DISPLAY, fontSize: 16 }}>
                        {balance.toLocaleString("ru-RU")} бонусов
                    </p>
                    {step && (
                        <p className="text-white/40 text-[12px]" style={{ fontFamily: BODY }}>
                            {step}
                        </p>
                    )}
                </div>
            </motion.div>

            {/* Input card */}
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="rounded-[22px] border border-white/10 bg-white/[0.05] p-6 flex flex-col gap-5">
                <p className="text-white/55 text-[14px] leading-relaxed" style={{ fontFamily: BODY }}>
                    Введи промокод — только латинские буквы и цифры
                </p>

                {/* Large code input */}
                <input
                    type="text"
                    value={code}
                    onChange={handleChange}
                    onKeyDown={e => e.key === "Enter" && !disabled && submit()}
                    placeholder="ПРОМОКОД"
                    maxLength={32}
                    className="w-full bg-transparent border-b-2 py-3 text-center text-white text-[28px] outline-none transition-colors placeholder:text-white/15 focus:border-white/40"
                    style={{
                        fontFamily: MONO,
                        fontWeight: 800,
                        letterSpacing: "0.12em",
                        borderColor: status === "error"
                            ? "rgba(228,32,107,0.6)"
                            : status === "success"
                            ? "rgba(41,204,106,0.6)"
                            : "rgba(255,255,255,0.15)",
                    }}
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                />

                {/* Submit button */}
                <motion.button
                    whileHover={disabled ? {} : { scale: 1.02 }}
                    whileTap={disabled ? {} : { scale: 0.97 }}
                    onClick={submit}
                    disabled={disabled}
                    className="w-full py-4 rounded-[14px] text-[14px] transition-all"
                    style={{
                        fontFamily: MONO,
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        background: disabled ? "rgba(255,255,255,0.06)" : "#349be6",
                        color: disabled ? "rgba(255,255,255,0.3)" : "#fff",
                        boxShadow: disabled ? "none" : "0 0 24px rgba(52,155,230,0.4)",
                    }}>
                    {status === "loading" ? "Проверяем..." : "Активировать"}
                </motion.button>

                {/* Status message */}
                <AnimatePresence mode="wait">
                    {status === "success" && (
                        <motion.div
                            key="success"
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className="flex items-center gap-2 rounded-[12px] px-4 py-3"
                            style={{ background: "rgba(41,204,106,0.12)", border: "1px solid rgba(41,204,106,0.3)" }}>
                            <span className="text-[18px]">✅</span>
                            <p
                                className="text-[13px]"
                                style={{ fontFamily: BODY, color: "#29cc6a" }}>
                                {message}
                            </p>
                        </motion.div>
                    )}
                    {status === "error" && (
                        <motion.div
                            key="error"
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className="flex items-center gap-2 rounded-[12px] px-4 py-3"
                            style={{ background: "rgba(228,32,107,0.1)", border: "1px solid rgba(228,32,107,0.3)" }}>
                            <span className="text-[18px]">❌</span>
                            <p
                                className="text-[13px]"
                                style={{ fontFamily: BODY, color: "#e4206b" }}>
                                {message}
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>

            {/* Help text */}
            <p
                className="text-center text-white/25 text-[12px] leading-relaxed"
                style={{ fontFamily: MONO }}>
                Промокоды действуют только в рамках своего кинотеатра
            </p>
        </div>
    )
}
